<template>
    <div>
        <CCol sm="12">
            <CCard>
                <CCardBody class="d-flex flex-column">
                    <div class="d-flex mb-2 justify-content-end align-items-start">
                        <!-- Title -->
                        <span class="flex-grow-1" v-if="$route.query && $route.query.key"><b>{{ $t('memberIntro') }}</b> {{$route.query.key}}</span>
                        <!-- Language dropdown -->
                        <LanguageDropdown v-model="currentLanguage"></LanguageDropdown>
                    </div>
                    <div class="d-flex flex-column">
                        <!-- Titles (multilingual) -->
                        <div role="group" class="form-group col-12">
                            <label class=""> {{ $t('titlesMandatory') }}</label>
                            <CInput  type="text" v-model="serviceValue.titles[currentLanguage.key]" :invalid-feedback="errorOutput['Titles']" :is-valid="genericValidator(serviceValue.titles[currentLanguage.key]) && errorValidator('Titles')"></CInput>
                        </div>
    
                        <!-- Service Value Infos (Vertical Layout) -->
                        <div v-for="(info, index) in serviceValue.serviceValueInfos" :key="index" class="d-flex flex-column mb-4 p-3 border rounded">
                            <!-- Delete Button -->
                            <div class="d-flex justify-content-end mt-2">
                                <CButton color="danger" @click="removeServiceValueInfo(index)">{{ $t('delete') }}</CButton>
                            </div>
                            <!-- Icon -->
                            <div role="group" class="form-group col-12">
                                <label class=""> {{ $t('icon') }}</label>
                                <CInput type="text" v-model="info.icon" :invalid-feedback="errorOutput['Icon']" :is-valid="genericValidator(info.icon) && errorValidator('Icon')"></CInput>
                            </div>
                            <!-- Title -->
                            <div role="group" class="form-group col-12">
                                <label class=""> {{ $t('title') }}</label>
                                <CInput type="text" v-model="info.titles[currentLanguage.key]" :invalid-feedback="errorOutput['Titles']" :is-valid="genericValidator(info.titles[currentLanguage.key]) && errorValidator('Titles')"></CInput>
                            </div>
                            <!-- Description -->
                            <div role="group" class="form-group col-12">
                                <label class=""> {{ $t('description') }}</label>
                                <vue-editor v-model="info.descriptions[currentLanguage.key]" v-bind:class="{'is-invalid': genericValidator(info.descriptions[currentLanguage.key]) == false || errorValidator(info.descriptions[currentLanguage.key]) }"></vue-editor>
                                <div v-if="errorOutput.Description" class="invalid-feedback">{{errorOutput.Description}}</div>
                            </div>
                            <!-- Position -->
                            <div role="group" class="form-group col-12">
                                <label class=""> {{ $t('positionMandatory') }}</label>
                                <CInput type="number" v-model="info.position" :invalid-feedback="errorOutput['Position']" :is-valid="genericValidator(info.position) && errorValidator('Position')"></CInput>
                            </div>
                        </div>
    
                        <!-- Add New Row Button -->
                        <div class="d-flex justify-content-start mt-3">
                            <CButton color="secondary" @click="addServiceValueInfo">{{ $t('addService') }}</CButton>
                        </div>
                    </div>
                    <!-- Action button + submitting -->  
                    <div role="group" class="form-group col-12 d-flex justify-content-end mt-5">
                        <CSpinner v-if="submitting" variant="success" small class="align-self-end"></CSpinner>
                        <CButton v-else type="button" class="btn btn-primary" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
                    </div>
                    <!-- Loading -->
                    <FormLoading v-if="loading"></FormLoading>
                </CCardBody>
            </CCard>
        </CCol>
        <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup" v-model="showConfirmationPopup"></ConfirmationPopUp>
    </div>
</template>
    
<script>
import { genericValidator, errorValidator, deepClone } from "@/helpers/utils.js";
import ServiceValue from "@/classes/ServiceValue";
import { mapState, mapActions } from 'vuex'
import LanguageDropdown from "@/controls/LanguageDropdown.vue";
import { CONSTANTS } from '@/helpers/consts';
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import FormLoading from '@/controls/FormLoading.vue'
import { VueEditor } from "vue2-editor";

export default {
    components: { LanguageDropdown, ConfirmationPopUp, FormLoading, VueEditor },
    props: {
        baseServiceValue: {
            type: Object,
            default: null,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            triedSubmit: false,
            currentLanguage: CONSTANTS.supportedLanguages[0],
            serviceValue: new ServiceValue({}, []), // Initialize with empty titles and serviceValueInfos
            submitting: false,
            showConfirmationPopup: false,
            confirmationMessage: "",
        }
    },
    mounted: function() {
        this.resetServiceValue();
    },
    computed: {
        ...mapState({ 
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        errorOutput: function() {
            var result = {};

            if (this.alertType == 'alert-danger' && this.alertOutput && Array.isArray(this.alertOutput)) {
                result = this.alertOutput.reduce(function(accum, currentVal) {
                    accum[currentVal.key] = currentVal.value;
                    return accum;
                }, {});
            }

            // Validate titles (multilingual)
            for (const lang of CONSTANTS.supportedLanguages) {
                if (!this.serviceValue.titles[lang.key]) result['Titles'] = this.$i18n.t("validateTitles");
            }

            // Validate serviceValueInfos
            for (const info of this.serviceValue.serviceValueInfos) {
                if (!info.icon || !info.position) {
                    result['Icon'] = this.$i18n.t("validateIcon");
                    result['Position'] = this.$i18n.t("validatePosition");
                }

                for (const lang of CONSTANTS.supportedLanguages) {
                    if (!info.titles[lang.key]) result['Titles'] = this.$i18n.t("validateTitles");
                    if (!info.descriptions[lang.key]) result['Description'] = this.$i18n.t("validateDescription");
                }
            }

            return result;
        },
        ...mapState('serviceValue', ['putServiceValueOutput']),
        ...mapState('serviceValue', ['postServiceValueOutput']),
    },
    methods: {
        resetServiceValue() {
            if (!this.baseServiceValue) {
                // Initialize titles and serviceValueInfos
                const titles = {};
                const serviceValueInfos = [{
                    icon: '',
                    titles: {},
                    descriptions: {},
                    position: 0 // Initialize position
                }];
                CONSTANTS.supportedLanguages.forEach(lang => {
                    titles[lang.key] = "";
                    serviceValueInfos[0].titles[lang.key] = "";
                    serviceValueInfos[0].descriptions[lang.key] = "";
                });

                this.serviceValue = new ServiceValue(titles, serviceValueInfos);
            } else {
                this.serviceValue = deepClone(new ServiceValue, this.baseServiceValue);
            }
        },
        addServiceValueInfo() {
            const newInfo = {
                icon: '',
                titles: {},
                descriptions: {},
                position: 0 // Initialize position
            };
            CONSTANTS.supportedLanguages.forEach(lang => {
                newInfo.titles[lang.key] = "";
                newInfo.descriptions[lang.key] = "";
            });
            this.serviceValue.serviceValueInfos.push(newInfo);
        },
        removeServiceValueInfo(index) {
            this.serviceValue.serviceValueInfos.splice(index, 1);
        },
        submitClicked() {
            this.triedSubmit = true;

            this.serviceValue.serviceValueInfos = this.serviceValue.serviceValueInfos.map(info => {
                return {
                    ...info,
                    position: parseInt(info.position, 10) // Convert position to integer
                };
            });

            // Emit event
            if (this.serviceValue.isValid()) {
                this.submitting = true;
                this.$emit('submit-requested', this.serviceValue);
            }
        },
        genericValidator,
        errorValidator,
        onClickConfirmationPopup(value) {
            this.showConfirmationPopup = value;     
            this.$router.push({name: "ServiceValueList" });
        },
    },
    watch: {
        putServiceValueOutput: function(val) {
            if (val) {
                this.showConfirmationPopup = true;
            }
        },
        postServiceValueOutput: function(val) {
            if (val) {
                this.showConfirmationPopup = true;
            }
        },
        baseServiceValue: function() {
            this.resetServiceValue();
        },
        alertType: function() {
            this.submitting = false;
        },
        alertOutput: function() {
            this.submitting = false;

            // Show pop up in case it is a string error
            // In case it is an array, it is shown on the field
            if (typeof this.alertOutput === 'string' || this.alertOutput instanceof String) {
                alert(this.alertOutput);
            }
        }
    }
}
</script>
    
    <style scoped>
        .showDropdown {
            display: block !important;
        }
    
        .input-group-append {
            height: fit-content;
        }
    
        img {
            width: 50%;
            height: 50%;
        }
    </style>